import React from 'react';
import { useBranchDetailsQuery } from '@/api/queries';
import { getUser } from '@/utils';
import {
    getBranchScopeMode,
    resolveAddressBranch,
    toStockFiguresScope,
    type AddressBranch,
    type BranchFilterOption,
    type ProductBranchScope,
    type StockFiguresScope,
} from './branchScopeMeta';
import { nameOfOption } from './productFiltersMeta';
import {
    envelopeOptionsStatus,
    useFilterOptionsState,
} from './useFilterOptionsState';

export type ProductBranchScopeState = {
    /** The branch the list asks for, from the address's, for this caller. */
    addressBranch: AddressBranch;
    /** The organisation's branches, for the branch tag's name. */
    branches: BranchFilterOption[];
    /** The drawer's branch field. */
    branchScope: ProductBranchScope;
    /** Whose stock the figures on screen count, for the Cost header. */
    stockFigures: StockFiguresScope;
};

/**
 * Whose stock the Products list's figures count (bos-app#118), from the
 * session's role and the branch in the page address: which branch the list
 * asks for, what the drawer shows for it, and what the Cost header says.
 * The product details page reads its own address the same way
 * (bos-app#119), so a branch means the same thing on both screens.
 *
 * The organisation's branches feed the OrgAdmin's picker and name the
 * BranchAdmin's branch; without a session they aren't read.
 */
export const useProductBranchScope = (
    addressBranchId: string | null,
): ProductBranchScopeState => {
    // Read once from the persisted session, which doesn't change under a
    // mounted page.
    const user = React.useMemo(() => getUser(), []);
    const mode = getBranchScopeMode(user);
    const ownBranchId = user?.branchId ?? null;

    const branchesQuery = useBranchDetailsQuery(undefined, mode !== 'none');
    const branches = React.useMemo<BranchFilterOption[]>(
        () =>
            branchesQuery.data?.success
                ? branchesQuery.data.response.items.map((branch) => ({
                      id: branch.id,
                      name: branch.name,
                  }))
                : [],
        [branchesQuery.data],
    );
    const options = useFilterOptionsState(
        branches,
        envelopeOptionsStatus(branchesQuery),
        branchesQuery.refetch,
    );

    const addressBranch = resolveAddressBranch(mode, addressBranchId, options);

    const branchScope = React.useMemo<ProductBranchScope>(() => {
        switch (mode) {
            case 'pick':
                return { mode, options };
            case 'fixed':
                return {
                    mode,
                    branchName: nameOfOption(branches, ownBranchId),
                };
            case 'none':
                return { mode };
        }
    }, [branches, mode, options, ownBranchId]);

    // The applied scope: a BranchAdmin's own branch, or the branch the list
    // asks for. The tooltip describes the figures on screen, not the drawer's
    // held pick.
    const stockFigures = React.useMemo(
        () =>
            toStockFiguresScope(
                mode === 'fixed' ? ownBranchId : addressBranch.branchId,
                branches,
            ),
        [addressBranch.branchId, branches, mode, ownBranchId],
    );

    return { addressBranch, branches, branchScope, stockFigures };
};
